/**
 * `board-grid` validators (design §17): cells in bounds, one tile per cell, tiles drawn
 * from the palette. Game-specific board rules (e.g. match-3 "no starting matches") layer
 * on later via per-game hooks; these are the kind-level invariants.
 */
import type { LevelFinding, LevelValidationCtx } from '../../types.js';
import type { BoardGridLevel } from './schema.js';

export function validateBoardGrid(data: BoardGridLevel, _ctx: LevelValidationCtx): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const palette = new Set(data.palette);
  const seen = new Set<string>();

  for (const cell of data.cells) {
    if (cell.x >= data.width || cell.y >= data.height) {
      findings.push({
        severity: 'error',
        code: 'cell-out-of-bounds',
        message: `cell (${cell.x},${cell.y}) is outside the ${data.width}×${data.height} board`,
      });
    }
    const key = `${cell.x},${cell.y}`;
    if (seen.has(key)) {
      findings.push({
        severity: 'error',
        code: 'duplicate-cell',
        message: `cell (${cell.x},${cell.y}) is set more than once`,
      });
    }
    seen.add(key);
    if (!palette.has(cell.tile)) {
      findings.push({
        severity: 'error',
        code: 'unknown-tile',
        message: `cell (${cell.x},${cell.y}) uses tile "${cell.tile}" which is not in the palette`,
      });
    }
  }

  if (data.cells.length === 0) {
    findings.push({
      severity: 'warn',
      code: 'empty-board',
      message: 'the board has no cells painted yet',
    });
  }
  return findings;
}
